import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gift, Copy, Check, Share2, Users, Wallet } from "lucide-react";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/referrals").then(({ data }) => setData(data)); }, []);

  if (!data) return <div className="p-10"><span className="hw-pulse-dot" /></div>;

  const referralLink = `${window.location.origin}/register/traveller?ref=${data.code}`;
  const shareText = `I'm loving SwaanStay for hotel bookings — negotiate any price! Sign up with my code ${data.code} and we both get ₹100 wallet credit. ${referralLink}`;

  const copy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "SwaanStay", text: shareText, url: referralLink });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-label text-blue-700">Grow together</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">Refer & Earn</h1>
        <p className="text-slate-600 mt-2">Invite friends to SwaanStay. When they sign up, you both get ₹100 in your wallet — instantly.</p>

        {/* Referral code card */}
        <div className="mt-8 relative overflow-hidden rounded-md p-6 text-white hw-shadow-pop bg-gradient-to-br from-blue-800 to-emerald-700" data-testid="referral-code-card">
          <div className="flex items-center gap-2 text-white/80"><Gift className="w-4 h-4" /><span className="hw-label">Your referral code</span></div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="hw-head text-4xl md:text-5xl font-black tracking-wider bg-white/10 border-2 border-white/30 px-5 py-3 rounded-md" data-testid="referral-code">{data.code}</div>
            <Button onClick={copy} className="bg-white text-blue-700 hover:bg-slate-100 rounded-sm font-bold" data-testid="referral-copy-btn">
              {copied ? <><Check className="w-4 h-4 mr-1" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy link</>}
            </Button>
            <Button onClick={shareWhatsapp} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm font-bold" data-testid="referral-whatsapp-btn">
              <Share2 className="w-4 h-4 mr-1" />WhatsApp
            </Button>
            <Button onClick={nativeShare} variant="outline" className="rounded-sm border-white text-white bg-transparent hover:bg-white/10" data-testid="referral-share-btn">
              Share
            </Button>
          </div>
          <div className="text-xs text-white/80 mt-4 break-all">{referralLink}</div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="hw-card p-5" data-testid="referral-count-card">
            <div className="hw-label text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> Signed up</div>
            <div className="hw-head text-3xl font-black text-slate-900 mt-1" data-testid="referral-count">{data.referred.length}</div>
          </div>
          <div className="hw-card p-5" data-testid="referral-bonus-card">
            <div className="hw-label text-slate-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> Total bonus earned</div>
            <div className="hw-head text-3xl font-black text-emerald-700 mt-1" data-testid="referral-bonus">₹{data.total_bonus.toLocaleString("en-IN")}</div>
          </div>
        </div>

        {/* How it works */}
        <div className="mt-8 hw-card p-6" data-testid="referral-how-it-works">
          <h2 className="hw-head text-2xl font-black text-slate-900">How it works</h2>
          <ol className="mt-4 space-y-3">
            {[
              { n: 1, t: "Share your code", d: "Send your link to friends via WhatsApp, Instagram, or anywhere." },
              { n: 2, t: "They sign up", d: "Your friend enters your code during signup — they get ₹100 wallet credit instantly." },
              { n: 3, t: "You earn ₹100", d: "The moment they sign up, ₹100 lands in your wallet. Use it on any booking." },
            ].map((s) => (
              <li key={s.n} className="flex gap-3">
                <div className="w-8 h-8 bg-blue-700 text-white grid place-items-center rounded-full font-black text-sm flex-shrink-0">{s.n}</div>
                <div>
                  <div className="hw-head font-bold text-slate-900">{s.t}</div>
                  <div className="text-sm text-slate-600">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Referred friends */}
        <div className="mt-8">
          <h2 className="hw-head text-2xl font-black text-slate-900">Your referrals</h2>
          {data.referred.length === 0 ? (
            <div className="hw-card p-8 mt-4 text-center" data-testid="referral-list-empty">
              <p className="text-slate-600">No referrals yet — share your code to start earning.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-2" data-testid="referral-list">
              {data.referred.map((r) => (
                <div key={r.user_id} className="hw-card p-4 flex items-center justify-between" data-testid={`referral-${r.user_id}`}>
                  <div>
                    <div className="font-semibold text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                  </div>
                  <div className="text-xs text-slate-500">Joined {new Date(r.created_at).toLocaleDateString("en-IN")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
